import { FilterColumn } from './types';
import { ViewSort } from './viewDefinition';

// Client-side sort for the unified list-filters program (spec 2026-07-22). Sort state lives in the
// URL (`sort`/`dir`); this applies it to the fetched array. Comparator mirrors the repo's prior
// useTableFeatures behaviour (nulls last, dates, numbers, case-insensitive strings) so converting a
// page to URL-driven sort doesn't change ordering.

function compareValues(a: unknown, b: unknown, direction: 'asc' | 'desc'): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1; // nulls always sort to the end
  if (b == null) return -1;

  if (a instanceof Date && b instanceof Date) {
    return direction === 'asc' ? a.getTime() - b.getTime() : b.getTime() - a.getTime();
  }

  if (typeof a === 'string' && typeof b === 'string') {
    const dateA = Date.parse(a);
    const dateB = Date.parse(b);
    if (!isNaN(dateA) && !isNaN(dateB) && a.includes('-') && b.includes('-')) {
      return direction === 'asc' ? dateA - dateB : dateB - dateA;
    }
  }

  if (typeof a === 'number' && typeof b === 'number') {
    return direction === 'asc' ? a - b : b - a;
  }

  const strA = String(a).toLowerCase();
  const strB = String(b).toLowerCase();
  return direction === 'asc' ? strA.localeCompare(strB) : strB.localeCompare(strA);
}

/**
 * Return a new array of `items` sorted by `sort` (or the original order when `sort` is null). The
 * sort column is resolved against `columns` by `sortKey` (falling back to `key`); its `getValue`
 * accessor supplies the compared value.
 */
export function sortItems<T>(items: T[], sort: ViewSort | null, columns: FilterColumn<T>[]): T[] {
  if (!sort) return items;
  const col = columns.find((c) => c.sortKey === sort.col) ?? columns.find((c) => c.key === sort.col);
  const get = col?.getValue;
  if (!get) return items;
  return [...items].sort((a, b) => compareValues(get(a), get(b), sort.dir));
}
